/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {PageTitle} from '../../../_metronic/layout/core/PageData'
import VehicleLiveLocation from '../../components/Dashboard/VehicleLiveLocation'
import {enqueueGeocode} from '../../map/nominatimGeocode'

type AlertSeverity = 'success' | 'warning' | 'danger' | 'info'
type AlertEvent = {
  id: string
  type: 'overspeed' | 'started_moving' | 'stopped' | 'online' | 'offline' | 'long_idle'
  severity: AlertSeverity
  title: string
  vehicle: string
  at: number
}

const MAX_ALERTS = 30
const ALERT_TTL_MS = 90 * 1000
const SPEED_LIMIT_KMH = 80
const OFFLINE_THRESHOLD_MS = 6 * 3600 * 1000

const vehicleKey = (item: any) => String(item?.device_name || item?.name || item?.id || '')
const parseDurationToSec = (text: string = '') => {
  if (!text || typeof text !== 'string') return 0
  const h = Number((text.match(/(\d+)\s*h/) || [])[1] || 0)
  const m = Number((text.match(/(\d+)\s*min/) || [])[1] || 0)
  const s = Number((text.match(/(\d+)\s*s/) || [])[1] || 0)
  return h * 3600 + m * 60 + s
}
const toNum = (value: any, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}
const parseTrackTime = (input: any): number => {
  if (input === null || input === undefined || input === '') return NaN
  if (typeof input === 'number' && Number.isFinite(input))
    return input < 1e12 ? input * 1000 : input
  const raw = String(input).trim()
  if (!raw) return NaN
  const direct = new Date(raw).getTime()
  if (Number.isFinite(direct)) return direct
  const isoLike = raw.replace(' ', 'T')
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoLike) ? isoLike : `${isoLike}Z`
  const fallback = new Date(withZone).getTime()
  return Number.isFinite(fallback) ? fallback : NaN
}
const cleanAddress = (text: string): string => {
  if (!text) return ''
  const PLUS_CODE_PREFIX = /^\s*[A-Z0-9]{4,8}\+[A-Z0-9]{2,4}\s*,\s*/i
  const PLUS_CODE_ONLY = /^\s*[A-Z0-9]{4,8}\+[A-Z0-9]{2,4}\s*$/i
  let cleaned = text.replace(PLUS_CODE_PREFIX, '').trim()
  if (PLUS_CODE_ONLY.test(cleaned)) cleaned = ''
  return cleaned
}
const formatLatLng = (lat: number, lng: number) => `${lat.toFixed(5)}, ${lng.toFixed(5)}`
const shortenAddress = (text: string): string => {
  const cleaned = cleanAddress(text)
  if (!cleaned) return ''
  const parts = cleaned
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length <= 2) return cleaned
  const first = parts[0]
  const second = parts[1]
  const last = parts[parts.length - 1]
  return second.toLowerCase() === last.toLowerCase()
    ? `${first}, ${second}`
    : `${first}, ${second}, ${last}`
}

const DashboardWrapper: FC = () => {
  const [liveData, setLiveData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null)
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0)
  const [streamStatus, setStreamStatus] = useState<'live' | 'fallback'>('fallback')
  const [alerts, setAlerts] = useState<AlertEvent[]>([])
  const [vehicleAddresses, setVehicleAddresses] = useState<Record<string, string>>({})
  const geocodeRequestedRef = useRef<Set<string>>(new Set())
  const geocodeLastAttemptRef = useRef<Record<string, number>>({})
  const vehicleAddressesRef = useRef<Record<string, string>>({})
  const prevDataRef = useRef<Record<string, any>>({})
  const isFirstFrameRef = useRef(true)

  useEffect(() => {
    vehicleAddressesRef.current = vehicleAddresses
  }, [vehicleAddresses])
  const focusVehicleOnMap = useCallback((vehicleName: string, openModal: boolean) => {
    if (!vehicleName || typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent('vms:focus-vehicle', {detail: {vehicleName, openModal}}))
  }, [])

  useEffect(() => {
    const onLiveData = (evt: any) => {
      const payload = evt?.detail || {}
      if (!Array.isArray(payload.data)) return
      setLiveData(payload.data)
      setLastUpdatedAt(Number(payload.updatedAt) || Date.now())
      setStreamStatus('live')
      setIsLoading(false)
    }
    window.addEventListener('vms:live-data', onLiveData)
    return () => window.removeEventListener('vms:live-data', onLiveData)
  }, [])
  useEffect(() => {
    const id = setInterval(() => {
      if (lastUpdatedAt) setSecondsSinceUpdate(Math.floor((Date.now() - lastUpdatedAt) / 1000))
    }, 1000)
    return () => clearInterval(id)
  }, [lastUpdatedAt])

  useEffect(() => {
    if (!liveData.length) return
    const prev = prevDataRef.current
    const next: Record<string, any> = {}
    const newAlerts: AlertEvent[] = []
    const now = Date.now()
    const seenKeys = new Set<string>()
    liveData.forEach((item) => {
      const key = vehicleKey(item)
      if (!key) return
      seenKeys.add(key)
      next[key] = item
      if (isFirstFrameRef.current) return
      const before = prev[key]
      const speedNow = toNum(item?.speed, 0)
      const speedBefore = toNum(before?.speed, 0)
      const stopSecNow = parseDurationToSec(String(item?.stop_duration || ''))
      const stopSecBefore = parseDurationToSec(String(before?.stop_duration || ''))
      const trackTimeNow = parseTrackTime(item?.time || item?.device_time || item?.datetime)
      const trackTimeBefore = before
        ? parseTrackTime(before?.time || before?.device_time || before?.datetime)
        : NaN
      const offlineNow = Number.isFinite(trackTimeNow)
        ? now - trackTimeNow > OFFLINE_THRESHOLD_MS
        : false
      const offlineBefore = Number.isFinite(trackTimeBefore)
        ? now - trackTimeBefore > OFFLINE_THRESHOLD_MS
        : false
      const name = String(item?.name || '-')
      if (speedNow > SPEED_LIMIT_KMH && speedBefore <= SPEED_LIMIT_KMH)
        newAlerts.push({
          id: `${key}-overspeed-${now}`,
          type: 'overspeed',
          severity: 'danger',
          title: `Overspeed ? ${Math.round(speedNow)} km/h`,
          vehicle: name,
          at: now,
        })
      if (!before && !offlineNow)
        newAlerts.push({
          id: `${key}-online-${now}`,
          type: 'online',
          severity: 'success',
          title: 'Came online',
          vehicle: name,
          at: now,
        })
      if (before) {
        if (speedBefore <= 3 && speedNow > 3)
          newAlerts.push({
            id: `${key}-move-${now}`,
            type: 'started_moving',
            severity: 'info',
            title: 'Started moving',
            vehicle: name,
            at: now,
          })
        if (speedBefore > 3 && speedNow <= 3)
          newAlerts.push({
            id: `${key}-stop-${now}`,
            type: 'stopped',
            severity: 'warning',
            title: 'Stopped',
            vehicle: name,
            at: now,
          })
        if (!offlineBefore && offlineNow)
          newAlerts.push({
            id: `${key}-offline-${now}`,
            type: 'offline',
            severity: 'danger',
            title: 'Went offline',
            vehicle: name,
            at: now,
          })
        if (offlineBefore && !offlineNow)
          newAlerts.push({
            id: `${key}-online-${now}`,
            type: 'online',
            severity: 'success',
            title: 'Came online',
            vehicle: name,
            at: now,
          })
        if (stopSecBefore < 1800 && stopSecNow >= 1800 && speedNow <= 3)
          newAlerts.push({
            id: `${key}-idle-${now}`,
            type: 'long_idle',
            severity: 'warning',
            title: `Idle ${Math.floor(stopSecNow / 60)} min`,
            vehicle: name,
            at: now,
          })
      }
    })
    if (!isFirstFrameRef.current)
      Object.keys(prev).forEach((key) => {
        if (seenKeys.has(key)) return
        const item = prev[key]
        newAlerts.push({
          id: `${key}-offline-${now}`,
          type: 'offline',
          severity: 'danger',
          title: 'Went offline',
          vehicle: String(item?.name || '-'),
          at: now,
        })
      })
    prevDataRef.current = next
    isFirstFrameRef.current = false
    if (newAlerts.length)
      setAlerts((cur) =>
        [...newAlerts, ...cur.filter((a) => now - a.at < ALERT_TTL_MS)].slice(0, MAX_ALERTS)
      )
  }, [liveData])

  useEffect(() => {
    if (!alerts.length) return
    const id = setInterval(() => {
      const now = Date.now()
      setAlerts((cur) => cur.filter((a) => now - a.at < ALERT_TTL_MS))
    }, 5000)
    return () => clearInterval(id)
  }, [alerts.length])
  useEffect(() => {
    if (!liveData.length) return
    const next: Record<string, string> = {}
    liveData.forEach((item) => {
      const key = vehicleKey(item)
      const addr = shortenAddress(String(item?.address || item?.location || ''))
      const lat = Number(item?.latitude)
      const lng = Number(item?.longitude)
      const coords = Number.isFinite(lat) && Number.isFinite(lng) ? formatLatLng(lat, lng) : ''
      if (key) next[key] = addr || coords
    })
    if (Object.keys(next).length) setVehicleAddresses((prev) => ({...prev, ...next}))
  }, [liveData])

  const geocodePriorityVehicles = useMemo(() => {
    const ranked = [...liveData].map((item) => {
      const speed = toNum(item?.speed, 0)
      const stopSec = parseDurationToSec(String(item?.stop_duration || ''))
      const status = speed > 3 ? 'Moving' : stopSec > 1800 ? 'Stopped' : 'Idle'
      const timeValue = parseTrackTime(item?.time || item?.device_time || item?.datetime)
      const rank = status === 'Moving' ? 0 : status === 'Idle' ? 1 : 2
      return {
        item,
        rank,
        timeValue: Number.isFinite(timeValue) ? timeValue : 0,
      }
    })
    ranked.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : b.timeValue - a.timeValue))
    return ranked.map((x) => x.item)
  }, [liveData])

  useEffect(() => {
    if (!geocodePriorityVehicles.length) return
    const now = Date.now()
    // Geocode by the same order as Recent Activity (top rows first).
    geocodePriorityVehicles.slice(0, 15).forEach((item) => {
      const key = vehicleKey(item)
      if (!key) return
      const current = vehicleAddressesRef.current[key]
      const hasReadableAddress = current && !/^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(current)
      if (hasReadableAddress) return
      const lat = Number(item?.latitude)
      const lng = Number(item?.longitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      const coordKey = `${lat.toFixed(4)},${lng.toFixed(4)}`
      if (geocodeRequestedRef.current.has(coordKey)) return
      const lastAttempt = geocodeLastAttemptRef.current[coordKey] || 0
      // Retry after cooldown (helps recover from 429).
      if (now - lastAttempt < 120000) return
      geocodeLastAttemptRef.current[coordKey] = now
      geocodeRequestedRef.current.add(coordKey)
      enqueueGeocode(lat, lng)
        .then((addr: string) => {
          const cleaned = shortenAddress(String(addr || ''))
          if (cleaned) setVehicleAddresses((prev) => ({...prev, [key]: cleaned}))
          geocodeRequestedRef.current.delete(coordKey)
        })
        .catch(() => {
          geocodeRequestedRef.current.delete(coordKey)
        })
    })
  }, [geocodePriorityVehicles])

  const dashboardStats = useMemo(() => {
    const totalVehicles = liveData.length
    let movingNow = 0,
      idle = 0,
      stopped = 0,
      offline = 0,
      movingSpeedSum = 0,
      topSpeed = 0,
      overspeed = 0
    let topSpeedVehicle = ''
    const now = Date.now()
    liveData.forEach((item) => {
      const speed = toNum(item?.speed, 0)
      const stopSec = parseDurationToSec(String(item?.stop_duration || ''))
      const trackTime = parseTrackTime(item?.time || item?.device_time || item?.datetime)
      const isOffline = Number.isFinite(trackTime) ? now - trackTime > OFFLINE_THRESHOLD_MS : false
      if (isOffline) {
        offline += 1
        return
      }
      if (speed > topSpeed) {
        topSpeed = speed
        topSpeedVehicle = String(item?.name || '')
      }
      if (speed > SPEED_LIMIT_KMH) overspeed += 1
      if (speed > 3) {
        movingNow += 1
        movingSpeedSum += speed
        return
      }
      if (stopSec > 0 && stopSec <= 1800) {
        idle += 1
        return
      }
      stopped += 1
    })
    return {
      totalVehicles,
      movingNow,
      idle,
      stopped,
      offline,
      avgSpeed: movingNow > 0 ? Math.round(movingSpeedSum / movingNow) : 0,
      topSpeed: Math.round(topSpeed),
      topSpeedVehicle,
      overspeed,
    }
  }, [liveData])
  const recentActivity = useMemo(() => {
    const rows = [...liveData].map((item) => {
      const speed = toNum(item?.speed, 0)
      const stopSec = parseDurationToSec(String(item?.stop_duration || ''))
      const status = speed > 3 ? 'Moving' : stopSec > 1800 ? 'Stopped' : 'Idle'
      const timeValue = parseTrackTime(item?.time || item?.device_time || item?.datetime)
      const rank = status === 'Moving' ? 0 : status === 'Idle' ? 1 : 2
      const key = vehicleKey(item)
      const lat = Number(item?.latitude)
      const lng = Number(item?.longitude)
      const coords = Number.isFinite(lat) && Number.isFinite(lng) ? formatLatLng(lat, lng) : ''
      return {
        key,
        name: item?.name || '-',
        status,
        speedText: `${speed} km/h`,
        rank,
        timeValue: Number.isFinite(timeValue) ? timeValue : 0,
        address: vehicleAddresses[key] || coords,
      }
    })
    rows.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : b.timeValue - a.timeValue))
    return rows
  }, [liveData, vehicleAddresses])

  return (
    <>
      <PageTitle breadcrumbs={[]}>Dashboard</PageTitle>
      <div className='row g-3 mb-3'>
        <div className='col-6 col-md-4 col-xl'>
          <div className='card h-100'>
            <div className='card-body py-4'>
              <div className='small text-uppercase text-muted'>Total vehicles</div>
              <div className='fs-2 fw-bolder'>{dashboardStats.totalVehicles}</div>
              <div className='small text-muted'>Active in fleet</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-xl'>
          <div className='card h-100'>
            <div className='card-body py-4'>
              <div className='small text-uppercase text-muted'>Moving now</div>
              <div className='fs-2 fw-bolder text-success'>{dashboardStats.movingNow}</div>
              <div className='small text-muted'>Currently in motion</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-xl'>
          <div className='card h-100'>
            <div className='card-body py-4'>
              <div className='small text-uppercase text-muted'>Idle</div>
              <div className='fs-2 fw-bolder text-warning'>{dashboardStats.idle}</div>
              <div className='small text-muted'>Engine on, not moving</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-xl'>
          <div className='card h-100'>
            <div className='card-body py-4'>
              <div className='small text-uppercase text-muted'>Stopped</div>
              <div className='fs-2 fw-bolder text-danger'>{dashboardStats.stopped}</div>
              <div className='small text-muted'>Parked / engine off</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-xl'>
          <div className='card h-100'>
            <div className='card-body py-4'>
              <div className='small text-uppercase text-muted'>Avg speed</div>
              <div className='fs-2 fw-bolder text-info'>
                {dashboardStats.avgSpeed}
                <span className='fs-6 fw-normal text-muted ms-1'>km/h</span>
              </div>
              <div className='small text-muted'>Across moving vehicles</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-xl'>
          <div className='card h-100'>
            <div className='card-body py-4'>
              <div className='small text-uppercase text-muted'>Top speed</div>
              <div className='fs-2 fw-bolder text-primary'>
                {dashboardStats.topSpeed}
                <span className='fs-6 fw-normal text-muted ms-1'>km/h</span>
              </div>
              <div className='small text-muted text-truncate'>
                {dashboardStats.topSpeedVehicle || 'No active vehicle'}
              </div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-xl'>
          <div className='card h-100'>
            <div className='card-body py-4'>
              <div className='small text-uppercase text-muted'>Overspeed</div>
              <div
                className={`fs-2 fw-bolder ${
                  dashboardStats.overspeed > 0 ? 'text-danger' : 'text-muted'
                }`}
              >
                {dashboardStats.overspeed}
              </div>
              <div className='small text-muted'>Above {SPEED_LIMIT_KMH} km/h</div>
            </div>
          </div>
        </div>
      </div>
      {alerts.length > 0 && (
        <div className='card mb-3'>
          <div className='card-body py-2 px-3'>
            <div className='d-flex align-items-center'>
              <span className='badge badge-light-danger me-2 flex-shrink-0'>Live alerts</span>
              <div
                className='d-flex flex-nowrap gap-2 overflow-auto'
                style={{scrollbarWidth: 'thin'}}
              >
                {alerts.map((alert) => (
                  <button
                    key={alert.id}
                    type='button'
                    className={`badge badge-light-${alert.severity} border-0 d-inline-flex align-items-center flex-shrink-0`}
                    style={{padding: '0.5rem 0.75rem', cursor: 'pointer'}}
                    onClick={() => focusVehicleOnMap(alert.vehicle, true)}
                  >
                    <span className='fw-bold me-1'>{alert.vehicle}</span>
                    <span className='me-2'>? {alert.title}</span>
                    <span className='text-muted small'>
                      {Math.max(0, Math.floor((Date.now() - alert.at) / 1000))}s
                    </span>
                  </button>
                ))}
              </div>
              <button
                type='button'
                className='btn btn-sm btn-light ms-2 flex-shrink-0'
                onClick={() => setAlerts([])}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
      <div className='row g-3'>
        <div className='col-xl-9'>
          <div className='card'>
            <div className='card-header border-0 pt-4'>
              <div>
                <h3 className='card-title fw-bolder'>Fleet Tracker</h3>
                <div className='small text-muted mt-2'>
                  Live positions across Bangladesh ?{' '}
                  {streamStatus === 'live' ? 'streaming in real-time' : 'updated every 10s'}
                </div>
              </div>
              <div className='card-toolbar d-flex align-items-center gap-2'>
                {lastUpdatedAt && (
                  <span
                    className={`badge ${
                      secondsSinceUpdate > 30
                        ? 'badge-light-danger'
                        : secondsSinceUpdate > 15
                        ? 'badge-light-warning'
                        : 'badge-light-success'
                    }`}
                  >
                    updated {secondsSinceUpdate}s ago
                  </span>
                )}
                <span
                  className={`badge ${
                    streamStatus === 'live' ? 'badge-light-success' : 'badge-light-warning'
                  }`}
                >
                  {isLoading ? 'Refreshing?' : streamStatus === 'live' ? 'Live' : 'Polling'}
                </span>
              </div>
            </div>
            <div className='card-body p-0'>
              <VehicleLiveLocation />
            </div>
          </div>
        </div>
        <div className='col-xl-3'>
          <div className='card'>
            <div className='card-header py-3'>
              <h4 className='card-title fw-bold'>Recent Activity</h4>
            </div>
            <div className='card-body py-2' style={{maxHeight: '500px', overflowY: 'auto'}}>
              {recentActivity.length > 0 ? (
                recentActivity.map((row, index) => (
                  <div
                    key={`${row.name}-${index}`}
                    className='border-bottom py-2'
                    style={{paddingLeft: 8, cursor: 'pointer'}}
                    onClick={() => focusVehicleOnMap(row.name, false)}
                  >
                    <div className='d-flex justify-content-between align-items-center'>
                      <div>
                        <span
                          className={`badge me-2 ${
                            row.status === 'Moving'
                              ? 'badge-light-success'
                              : row.status === 'Stopped'
                              ? 'badge-light-danger'
                              : 'badge-light-warning'
                          }`}
                        >
                          {row.status}
                        </span>
                        <span className='small fw-bold'>{row.name}</span>
                      </div>
                      <span className='small text-muted'>{row.speedText}</span>
                    </div>
                    <div className='small text-muted text-truncate mt-1' title={row.address || ''}>
                      {row.address || 'No location'}
                    </div>
                  </div>
                ))
              ) : (
                <div className='text-muted small py-2'>No activity data available.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {DashboardWrapper}
export default DashboardWrapper
